import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Button, Paper, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { api, errorMessageFor } from "../../api/client";
import { endpoints } from "../../api/endpoints";

// Email is required and must parse as an email. otp and password are required
// too, but only once the OTP step is showing.
const schema = z.object({
  email: z.string().min(1, "This field is required").email("Invalid email format."),
  otp: z.string(),
  password: z.string(),
});

// "Send Email" stays disabled while the email is invalid, "Submit" while any
// field is empty, and the message appears once a field has been touched.

type ResetForm = z.infer<typeof schema>;

export function ForgetPasswordPage() {
  const navigate = useNavigate();
  const [otpSent, setOtpSent] = useState(false);
  const [notice, setNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const {
    register,
    getValues,
    trigger,
    control,
    formState: { errors },
  } = useForm<ResetForm>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { email: "", otp: "", password: "" },
  });

  // useWatch rather than watch(): watch() returns a fresh function each render,
  // which the React Compiler refuses to memoize around.
  const values = useWatch({ control });
  const emailInvalid = !z.string().email().safeParse(values.email).success;
  const otpStepIncomplete = !values.otp || !values.password;

  async function sendOtp() {
    if (!(await trigger("email"))) return;
    setBusy(true);
    setNotice(null);
    try {
      // The Auth service is strict about this shape.
      await api.post(endpoints.account.forgotPasswordOtp(), { otpFor: getValues("email") }, {
        silent: true,
      } as never);
      setOtpSent(true);
      setNotice({ kind: "success", text: "OTP sent to your email" });
    } catch (error) {
      setNotice({ kind: "error", text: errorMessageFor(error) ?? "Could not send the OTP." });
    } finally {
      setBusy(false);
    }
  }

  async function changePassword() {
    const { email, otp, password } = getValues();
    if (!otp || !password) {
      setNotice({ kind: "error", text: "This field is required" });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      // The service expects the whole form: { email, password, otp }.
      await api.post(endpoints.account.resetPassword(), { email, password, otp }, {
        silent: true,
      } as never);
      navigate("/login", { replace: true });
    } catch (error) {
      setNotice({ kind: "error", text: errorMessageFor(error) ?? "Could not reset the password." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", p: 2 }}>
      <Paper sx={{ p: 4, width: "100%", maxWidth: 400 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Forgot Password
        </Typography>
        <Typography sx={{ mb: 2 }}>
          Enter your email and we’ll send a link with instruction on how to reset your password
        </Typography>

        {notice && (
          <Alert severity={notice.kind} sx={{ mb: 2 }} role="alert">
            {notice.text}
          </Alert>
        )}

        <TextField
          {...register("email")}
          label="Email"
          fullWidth
          margin="normal"
          disabled={otpSent}
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
        />

        {otpSent && (
          <>
            <TextField {...register("otp")} label="OTP" fullWidth margin="normal" />
            <TextField
              {...register("password")}
              label="Password"
              type="password"
              fullWidth
              margin="normal"
            />
            <Button onClick={sendOtp} disabled={busy || emailInvalid}>
              Resend OTP
            </Button>
          </>
        )}

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 2 }}
          disabled={busy || (otpSent ? otpStepIncomplete : emailInvalid)}
          onClick={otpSent ? changePassword : sendOtp}
        >
          {otpSent ? "Submit" : "Send Email"}
        </Button>

        <Typography sx={{ mt: 2 }}>
          <Link to="/login">Back to Login</Link>
        </Typography>
      </Paper>
    </Box>
  );
}
